"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { matchCatalog } from "@/lib/invoice/catalog";
import { fmtMoney } from "@/lib/invoice/utils";
import { Input, cx } from "./ui";

// Line-item description box with type-ahead: suggests saved services, projects and
// previously billed lines; picking one fills price, HSN/SAC, tax and details.
export default function ServiceInput({ value, onChange, onPick, onBlur, catalog, currency, placeholder, className }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const box = useRef(null);
  const matches = useMemo(() => (open ? matchCatalog(catalog, value) : []), [open, catalog, value]);
  const show = open && matches.length > 0;

  useEffect(() => {
    if (!open) return;
    const h = (e) => !box.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  // the list floats on <body> so the scrolling line-items table can't clip it
  useLayoutEffect(() => {
    if (!show) return;
    const place = () => {
      const r = box.current?.getBoundingClientRect();
      if (r) setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 320) });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [show]);

  const pick = (m) => {
    onPick(m.line);
    setOpen(false);
  };

  return (
    <div ref={box} className="relative">
      <Input
        className={className}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={show}
        onChange={(e) => {
          onChange(e.target.value);
          setHi(0);
          setOpen(true);
        }}
        onBlur={(e) => {
          // let a click on a suggestion land first
          setTimeout(() => setOpen(false), 120);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (!open || !matches.length) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            if (matches[hi]) {
              e.preventDefault();
              pick(matches[hi]);
            }
          } else if (e.key === "Escape") {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {show &&
        pos &&
        createPortal(
          <div role="listbox" style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width }} className="z-[60] max-h-72 overflow-auto rounded-lg bg-white py-1 text-sm shadow-pop ring-1 ring-slate-900/10">
            <div className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Saved services &amp; past items · Enter to fill</div>
            {matches.map((m, i) => (
              <div
                key={m.description + i}
                role="option"
                aria-selected={i === hi}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                onMouseEnter={() => setHi(i)}
                className={cx("cursor-pointer px-3 py-1.5", i === hi ? "bg-brand-50" : "")}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className={cx("truncate font-medium", i === hi ? "text-brand-800" : "text-slate-800")}>{m.description}</span>
                  <span className="shrink-0 tabular-nums text-xs text-slate-600">{m.line.rate ? fmtMoney(m.line.rate, m.currency || currency) : ""}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <span className="shrink-0 whitespace-nowrap rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase text-slate-500">{m.source}</span>
                  {m.line.hsn && <span>HSN/SAC {m.line.hsn}</span>}
                  {m.line.details && <span className="truncate">{m.line.details.split("\n")[0]}</span>}
                </div>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
